// React Query hooks for the outreach admin endpoints.
// Mirrors backend/app/routers/outreach.py.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '../api/client';
import type {
  AffinityListConfig,
  AffinityListConfigIn,
  AffinityListConfigUpdate,
  AffinityListCustomField,
  AffinityListCustomFieldIn,
  AffinityListCustomFieldUpdate,
  AffinityListField,
  RefreshMergeFieldsResult,
  Campaign,
  CampaignActionResult,
  CampaignAuditResponse,
  CampaignEventConfig,
  CampaignErrorRow,
  CampaignIn,
  CampaignRecipientsResponse,
  CampaignStats,
  CampaignStep,
  CampaignStepCreateIn,
  CampaignStepCreateOut,
  CampaignStepIn,
  CampaignTickEvent,
  CampaignUpdate,
  EventTestSendResult,
  FinishedRecipientImpact,
  ResumeFinishedImpact,
  ResumeFinishedResult,
  FormIn,
  FormResponsesResponse,
  FormUpdate,
  OutreachAsset,
  OutreachAssetUploadResult,
  OutreachForm,
  OutreachSender,
  OutreachSenderIn,
  OutreachSenderUpdate,
  OutreachSettings,
  OutreachSettingsUpdate,
  ReactivateCampaignRequest,
  ReactivateCampaignResult,
  RecipientBulkActionResult,
  RecipientBulkHardDeleteResult,
  RecipientRetryResult,
  RsvpApprovalActionResult,
  RsvpApprovalsResponse,
  RsvpBulkApproveResult,
  RsvpManualAction,
  StepDeletionImpact,
  StepTemplate,
  StepTemplateIn,
  StepTemplateUpdate,
  TestSendRequest,
  TestSendResult,
  TimelineEvent,
  UnmatchedReply,
} from '../types/outreach';

// ─────────────────────────────────────────────────────────────────────────
// Senders
// ─────────────────────────────────────────────────────────────────────────

export function useOutreachSenders() {
  return useQuery<OutreachSender[]>({
    queryKey: ['outreach', 'senders'],
    queryFn: () => apiFetch<OutreachSender[]>('/api/outreach/senders'),
    staleTime: 30 * 1000,
  });
}

export function useCreateOutreachSender() {
  const qc = useQueryClient();
  return useMutation<OutreachSender, Error, OutreachSenderIn>({
    mutationFn: (body) =>
      apiFetch<OutreachSender>('/api/outreach/senders', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach', 'senders'] }),
  });
}

export function useUpdateOutreachSender() {
  const qc = useQueryClient();
  return useMutation<
    OutreachSender,
    Error,
    { email: string; body: OutreachSenderUpdate }
  >({
    mutationFn: ({ email, body }) =>
      apiFetch<OutreachSender>(
        `/api/outreach/senders/${encodeURIComponent(email)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach', 'senders'] }),
  });
}

export function useDeleteOutreachSender() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (email) =>
      apiFetch<void>(`/api/outreach/senders/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach', 'senders'] }),
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Affinity list configs
// ─────────────────────────────────────────────────────────────────────────

export function useAffinityListConfigs() {
  return useQuery<AffinityListConfig[]>({
    queryKey: ['outreach', 'list-configs'],
    queryFn: () => apiFetch<AffinityListConfig[]>('/api/outreach/list-configs'),
    staleTime: 30 * 1000,
  });
}

export function useCreateListConfig() {
  const qc = useQueryClient();
  return useMutation<AffinityListConfig, Error, AffinityListConfigIn>({
    mutationFn: (body) =>
      apiFetch<AffinityListConfig>('/api/outreach/list-configs', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outreach', 'list-configs'] }),
  });
}

export function useUpdateListConfig() {
  const qc = useQueryClient();
  return useMutation<
    AffinityListConfig,
    Error,
    { listId: number; body: AffinityListConfigUpdate }
  >({
    mutationFn: ({ listId, body }) =>
      apiFetch<AffinityListConfig>(
        `/api/outreach/list-configs/${listId}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outreach', 'list-configs'] }),
  });
}

export function useDeleteListConfig() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (listId) =>
      apiFetch<void>(`/api/outreach/list-configs/${listId}`, {
        method: 'DELETE',
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outreach', 'list-configs'] }),
  });
}

/**
 * Re-fetch the Status + Scope (Campaign) dropdown option maps from
 * Affinity and overwrite the cached values on this list config. Called
 * from the Campaign → Overview → Edit flow so newly-added Affinity
 * options surface in the filter dropdowns without re-seeding.
 */
export function useRefreshListOptions() {
  const qc = useQueryClient();
  return useMutation<AffinityListConfig, Error, number>({
    mutationFn: (listId) =>
      apiFetch<AffinityListConfig>(
        `/api/outreach/list-configs/${listId}/refresh-options`,
        { method: 'POST' },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outreach', 'list-configs'] }),
  });
}

// ─────────────────────────────────────────────────────────────────────────
// List-scoped custom merge fields (Feature 2)
// ─────────────────────────────────────────────────────────────────────────

export function useListCustomFields(listId: number | null) {
  return useQuery<AffinityListCustomField[]>({
    queryKey: ['outreach', 'list-custom-fields', listId],
    queryFn: () =>
      apiFetch<AffinityListCustomField[]>(
        `/api/outreach/list-configs/${listId}/custom-fields`,
      ),
    enabled: listId != null && listId > 0,
    staleTime: 30 * 1000,
  });
}

export function useCreateListCustomField() {
  const qc = useQueryClient();
  return useMutation<
    AffinityListCustomField,
    Error,
    { listId: number; body: AffinityListCustomFieldIn }
  >({
    mutationFn: ({ listId, body }) =>
      apiFetch<AffinityListCustomField>(
        `/api/outreach/list-configs/${listId}/custom-fields`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({
        queryKey: ['outreach', 'list-custom-fields', vars.listId],
      }),
  });
}

export function useUpdateListCustomField() {
  const qc = useQueryClient();
  return useMutation<
    AffinityListCustomField,
    Error,
    { listId: number; cfId: number; body: AffinityListCustomFieldUpdate }
  >({
    mutationFn: ({ listId, cfId, body }) =>
      apiFetch<AffinityListCustomField>(
        `/api/outreach/list-configs/${listId}/custom-fields/${cfId}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({
        queryKey: ['outreach', 'list-custom-fields', vars.listId],
      }),
  });
}

export function useDeleteListCustomField() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { listId: number; cfId: number }
  >({
    mutationFn: ({ listId, cfId }) =>
      apiFetch<void>(
        `/api/outreach/list-configs/${listId}/custom-fields/${cfId}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({
        queryKey: ['outreach', 'list-custom-fields', vars.listId],
      }),
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Affinity field discovery (live-fetch)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Fetch field metadata for an Affinity list. Used by the list-config
 * editor to populate the status-field picker + the dropdown-option cache
 * when an admin registers a new list.
 *
 * Pass null to disable -- useful before the user has typed a list ID.
 */
export function useAffinityListFields(listId: number | null) {
  return useQuery<AffinityListField[]>({
    queryKey: ['outreach', 'affinity-list-fields', listId],
    queryFn: () =>
      apiFetch<AffinityListField[]>(
        `/api/outreach/affinity-list-fields/${listId}`,
      ),
    enabled: listId != null && listId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Campaigns
// ─────────────────────────────────────────────────────────────────────────

export function useCampaigns() {
  return useQuery<Campaign[]>({
    queryKey: ['outreach', 'campaigns'],
    queryFn: () => apiFetch<Campaign[]>('/api/outreach/campaigns'),
    staleTime: 30 * 1000,
  });
}

export function useCampaign(campaignId: number | null) {
  return useQuery<Campaign>({
    queryKey: ['outreach', 'campaigns', campaignId],
    queryFn: () =>
      apiFetch<Campaign>(`/api/outreach/campaigns/${campaignId}`),
    enabled: campaignId != null && campaignId > 0,
    staleTime: 30 * 1000,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation<Campaign, Error, CampaignIn>({
    mutationFn: (body) =>
      apiFetch<Campaign>('/api/outreach/campaigns', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outreach', 'campaigns'] }),
  });
}

export function useUpdateCampaign() {
  const qc = useQueryClient();
  return useMutation<
    Campaign,
    Error,
    { campaignId: number; body: CampaignUpdate }
  >({
    mutationFn: ({ campaignId, body }) =>
      apiFetch<Campaign>(`/api/outreach/campaigns/${campaignId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (_, { campaignId }) => {
      qc.invalidateQueries({ queryKey: ['outreach', 'campaigns'] });
      qc.invalidateQueries({ queryKey: ['outreach', 'campaigns', campaignId] });
    },
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (campaignId) =>
      apiFetch<void>(`/api/outreach/campaigns/${campaignId}`, {
        method: 'DELETE',
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outreach', 'campaigns'] }),
  });
}

function useCampaignAction(
  action: 'start' | 'pause' | 'finish' | 'enroll' | 'archive' | 'unarchive',
) {
  const qc = useQueryClient();
  return useMutation<CampaignActionResult, Error, number>({
    mutationFn: (campaignId) =>
      apiFetch<CampaignActionResult>(
        `/api/outreach/campaigns/${campaignId}/${action}`,
        { method: 'POST' },
      ),
    onSuccess: (_, campaignId) => {
      qc.invalidateQueries({ queryKey: ['outreach', 'campaigns'] });
      qc.invalidateQueries({ queryKey: ['outreach', 'campaigns', campaignId] });
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'recipients'],
      });
    },
  });
}

/**
 * Start (or resume) a campaign. Optional `force` flag bypasses the
 * pre-flight data-issue gate added in Feature 2 / Phase 3 — without
 * it, the backend returns 409 with a `DataIssueSummary` when any
 * recipient has a blank required custom field or (per_org) divergent
 * values.
 */
export function useStartCampaign() {
  const qc = useQueryClient();
  return useMutation<
    CampaignActionResult, Error,
    number | { campaignId: number; force?: boolean }
  >({
    mutationFn: (arg) => {
      const { campaignId, force } =
        typeof arg === 'number'
          ? { campaignId: arg, force: false }
          : arg;
      return apiFetch<CampaignActionResult>(
        `/api/outreach/campaigns/${campaignId}/start`,
        {
          method: 'POST',
          body: JSON.stringify({ force: !!force }),
        },
      );
    },
    onSuccess: (_, arg) => {
      const cid = typeof arg === 'number' ? arg : arg.campaignId;
      qc.invalidateQueries({ queryKey: ['outreach', 'campaigns'] });
      qc.invalidateQueries({ queryKey: ['outreach', 'campaigns', cid] });
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', cid, 'recipients'],
      });
    },
  });
}
export const usePauseCampaign = () => useCampaignAction('pause');
export const useFinishCampaign = () => useCampaignAction('finish');
export const useEnrollCampaign = () => useCampaignAction('enroll');
export const useArchiveCampaign = () => useCampaignAction('archive');
export const useUnarchiveCampaign = () => useCampaignAction('unarchive');

export function useCampaignRecipients(
  campaignId: number | null,
  statusFilter?: string,
  enrollmentKindFilter?: string,
) {
  const params = new URLSearchParams();
  if (statusFilter) params.set('status_filter', statusFilter);
  if (enrollmentKindFilter) {
    params.set('enrollment_kind_filter', enrollmentKindFilter);
  }
  const qs = params.toString() ? `?${params.toString()}` : '';
  return useQuery<CampaignRecipientsResponse>({
    queryKey: [
      'outreach', 'campaigns', campaignId, 'recipients',
      statusFilter || '', enrollmentKindFilter || '',
    ],
    queryFn: () =>
      apiFetch<CampaignRecipientsResponse>(
        `/api/outreach/campaigns/${campaignId}/recipients${qs}`,
      ),
    enabled: campaignId != null && campaignId > 0,
    staleTime: 15 * 1000,
    // Status flips silently when the sequencer fires or an RSVP comes
    // in — poll so the table doesn't go stale during a launch window.
    refetchInterval: 30 * 1000,
  });
}

export function useCampaignStats(
  campaignId: number | null,
  includeTest: boolean = false,
) {
  return useQuery<CampaignStats>({
    queryKey: ['outreach', 'campaigns', campaignId, 'stats', includeTest],
    queryFn: () => {
      const qs = includeTest ? '?include_test=true' : '';
      return apiFetch<CampaignStats>(
        `/api/outreach/campaigns/${campaignId}/stats${qs}`,
      );
    },
    enabled: campaignId != null && campaignId > 0,
    staleTime: 30 * 1000,
    // Auto-refresh while the Status tab is open. Same cadence as
    // useCampaignTicks (30s) so the stats cards + recent-activity
    // table stay in lockstep without manual refresh.
    refetchInterval: 30 * 1000,
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Campaign steps -- draft-only edits
// ─────────────────────────────────────────────────────────────────────────

function _invalidateCampaign(qc: ReturnType<typeof useQueryClient>, campaignId: number) {
  qc.invalidateQueries({ queryKey: ['outreach', 'campaigns'] });
  qc.invalidateQueries({ queryKey: ['outreach', 'campaigns', campaignId] });
}

export function useAddCampaignStep() {
  const qc = useQueryClient();
  return useMutation<
    CampaignStepCreateOut, Error,
    { campaignId: number; body: CampaignStepCreateIn }
  >({
    mutationFn: ({ campaignId, body }) =>
      apiFetch<CampaignStepCreateOut>(
        `/api/outreach/campaigns/${campaignId}/steps`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: (_, { campaignId }) => _invalidateCampaign(qc, campaignId),
  });
}

export function useUpdateCampaignStep() {
  const qc = useQueryClient();
  return useMutation<
    CampaignStep,
    Error,
    { campaignId: number; stepId: number; body: CampaignStepIn }
  >({
    mutationFn: ({ campaignId, stepId, body }) =>
      apiFetch<CampaignStep>(
        `/api/outreach/campaigns/${campaignId}/steps/${stepId}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    onSuccess: (_, { campaignId }) => _invalidateCampaign(qc, campaignId),
  });
}

export function useDeleteCampaignStep() {
  const qc = useQueryClient();
  return useMutation<
    void, Error,
    { campaignId: number; stepId: number }
  >({
    mutationFn: ({ campaignId, stepId }) =>
      apiFetch<void>(
        `/api/outreach/campaigns/${campaignId}/steps/${stepId}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_, { campaignId }) => _invalidateCampaign(qc, campaignId),
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Unmatched replies queue
// ─────────────────────────────────────────────────────────────────────────

export function useUnmatchedReplies() {
  return useQuery<UnmatchedReply[]>({
    queryKey: ['outreach', 'unmatched-replies'],
    queryFn: () =>
      apiFetch<UnmatchedReply[]>('/api/outreach/unmatched-replies'),
    staleTime: 30 * 1000,
  });
}

export function useLinkUnmatchedReply() {
  const qc = useQueryClient();
  return useMutation<
    UnmatchedReply, Error,
    { replyId: number; recipientId: number }
  >({
    mutationFn: ({ replyId, recipientId }) =>
      apiFetch<UnmatchedReply>(`/api/outreach/unmatched-replies/${replyId}`, {
        method: 'PATCH',
        body: JSON.stringify({ recipient_id: recipientId }),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outreach', 'unmatched-replies'] }),
  });
}

export function useDismissUnmatchedReply() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (replyId) =>
      apiFetch<void>(`/api/outreach/unmatched-replies/${replyId}`, {
        method: 'DELETE',
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outreach', 'unmatched-replies'] }),
  });
}

/**
 * Re-mirror every recipient's status from the campaign's list source.
 * The endpoint itself is source-routed (S3b): affinity-sourced campaigns
 * read Affinity V1, crm-sourced ones re-read the native list through the
 * governance seam.
 */
export function useRefreshListStatuses() {
  const qc = useQueryClient();
  return useMutation<
    { updated: number; unchanged: number; missing: number; errors: number },
    Error,
    number  // campaign_id
  >({
    mutationFn: (campaignId) =>
      apiFetch(
        `/api/outreach/campaigns/${campaignId}/refresh-affinity-statuses`,
        { method: 'POST' },
      ),
    onSuccess: (_data, campaignId) => {
      // Invalidate the recipients table so it re-fetches with fresh
      // current_list_status values. The key must match the one the
      // recipients query registers under — ['outreach','campaigns',
      // <id>,'recipients',...] — an ['outreach','recipients',<id>] key
      // matched nothing and left the table on the 30s poll.
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'recipients'],
      });
    },
  });
}

export function useDismissAllUnmatchedReplies() {
  const qc = useQueryClient();
  return useMutation<{ deleted: number }, Error, void>({
    mutationFn: () =>
      apiFetch<{ deleted: number }>('/api/outreach/unmatched-replies', {
        method: 'DELETE',
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outreach', 'unmatched-replies'] }),
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Settings (singleton; global test-mode toggle)
// ─────────────────────────────────────────────────────────────────────────

export function useOutreachSettings() {
  return useQuery<OutreachSettings>({
    queryKey: ['outreach', 'settings'],
    queryFn: () => apiFetch<OutreachSettings>('/api/outreach/settings'),
    staleTime: 30 * 1000,
  });
}

export function useUpdateOutreachSettings() {
  const qc = useQueryClient();
  return useMutation<OutreachSettings, Error, OutreachSettingsUpdate>({
    mutationFn: (body) =>
      apiFetch<OutreachSettings>('/api/outreach/settings', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outreach', 'settings'] }),
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Recipient bulk actions
// ─────────────────────────────────────────────────────────────────────────

export function useBulkRemoveRecipients() {
  const qc = useQueryClient();
  return useMutation<
    RecipientBulkActionResult, Error,
    { campaignId: number; recipientIds: number[] }
  >({
    mutationFn: ({ campaignId, recipientIds }) =>
      apiFetch<RecipientBulkActionResult>(
        `/api/outreach/campaigns/${campaignId}/recipients/bulk-remove`,
        {
          method: 'POST',
          body: JSON.stringify({ recipient_ids: recipientIds }),
        },
      ),
    onSuccess: (_, { campaignId }) => {
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'recipients'],
      });
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'stats'],
      });
    },
  });
}

/**
 * Re-pull every non-terminal recipient's Affinity field values and
 * rewrite their `merge_fields_json`. Used after an admin adds a new
 * list-scoped custom merge field so existing recipients pick up the
 * value without re-enrollment.
 */
export function useRefreshMergeFields() {
  const qc = useQueryClient();
  return useMutation<RefreshMergeFieldsResult, Error, number>({
    mutationFn: (campaignId) =>
      apiFetch<RefreshMergeFieldsResult>(
        `/api/outreach/campaigns/${campaignId}/refresh-merge-fields`,
        { method: 'POST' },
      ),
    onSuccess: (_, campaignId) => {
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'recipients'],
      });
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'stats'],
      });
    },
  });
}

export function useBulkRestoreRecipients() {
  const qc = useQueryClient();
  return useMutation<
    RecipientBulkActionResult, Error,
    { campaignId: number; recipientIds: number[] }
  >({
    mutationFn: ({ campaignId, recipientIds }) =>
      apiFetch<RecipientBulkActionResult>(
        `/api/outreach/campaigns/${campaignId}/recipients/bulk-restore`,
        {
          method: 'POST',
          body: JSON.stringify({ recipient_ids: recipientIds }),
        },
      ),
    onSuccess: (_, { campaignId }) =>
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'recipients'],
      }),
  });
}

export function useClearAllRecipients() {
  const qc = useQueryClient();
  return useMutation<RecipientBulkActionResult, Error, number>({
    mutationFn: (campaignId) =>
      apiFetch<RecipientBulkActionResult>(
        `/api/outreach/campaigns/${campaignId}/recipients`,
        { method: 'DELETE' },
      ),
    onSuccess: (_, campaignId) => {
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'recipients'],
      });
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'stats'],
      });
    },
  });
}

/**
 * Hard-delete a SUBSET of recipients (or whatever the unified Clear
 * button is scoped to). Cascades through send / reply / response /
 * tick-event children. Refuses recipients with replies or in-flight
 * sends -- the response includes a `refused` list with reasons.
 *
 * Empty recipient_ids is allowed and is a no-op (deleted=0, refused=[])
 * so the caller doesn't have to pre-check selection state.
 */
export function useBulkHardDeleteRecipients() {
  const qc = useQueryClient();
  return useMutation<
    RecipientBulkHardDeleteResult,
    Error,
    { campaignId: number; recipientIds: number[] }
  >({
    mutationFn: ({ campaignId, recipientIds }) =>
      apiFetch<RecipientBulkHardDeleteResult>(
        `/api/outreach/campaigns/${campaignId}/recipients/bulk-hard-delete`,
        { method: 'POST', body: JSON.stringify({ recipient_ids: recipientIds }) },
      ),
    onSuccess: (_, { campaignId }) => {
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'recipients'],
      });
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'stats'],
      });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Test-send (one-shot preview email)
// ─────────────────────────────────────────────────────────────────────────

export function useTestSendCampaign() {
  return useMutation<
    TestSendResult, Error,
    { campaignId: number; body: TestSendRequest }
  >({
    mutationFn: ({ campaignId, body }) =>
      apiFetch<TestSendResult>(
        `/api/outreach/campaigns/${campaignId}/test-send`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Status / monitoring (Layer 1+2+3)
// ─────────────────────────────────────────────────────────────────────────

export function useCampaignTicks(
  campaignId: number | null,
  { includeTest = false, limit = 50 }: {
    includeTest?: boolean;
    limit?: number;
  } = {},
) {
  return useQuery<CampaignTickEvent[]>({
    queryKey: ['outreach', 'campaigns', campaignId, 'ticks', includeTest, limit],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      if (includeTest) params.set('include_test', 'true');
      return apiFetch<CampaignTickEvent[]>(
        `/api/outreach/campaigns/${campaignId}/ticks?${params}`,
      );
    },
    enabled: campaignId != null && campaignId > 0,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,  // poll while open so the panel stays live
  });
}

// Reply forensics — full diagnostic dump for false-positive REPLIED
// investigation. Returns inbound headers + matched send + recipient
// for every campaign_replies row tied to a recipient on this
// campaign. Lazy: don't fetch until the panel is opened (the
// `enabled` flag gates it on a separate trigger from the parent).
export function useCampaignReplyForensics(
  campaignId: number | null,
  enabled: boolean,
) {
  return useQuery<{
    campaign_id: number;
    count: number;
    replies: Array<{
      reply_id: number;
      received_at: string;
      source: string;
      match_confidence: string;
      inbound: {
        from_email: string;
        in_reply_to: string | null;
        internet_message_id: string | null;
        body_excerpt: string | null;
        subject: string | null;
        auto_submitted: string | null;
        x_autoreply: string | null;
        precedence: string | null;
        raw_headers: Record<string, string[]> | null;
      };
      matched_send: {
        send_id: number;
        send_imid: string | null;
        sent_at: string;
        step_order: number | null;
        sent_to_email: string | null;
        sent_to_campaign_id: number | null;
        from_matches_sent_to: boolean;
      } | null;
      recipient: {
        id: number;
        email: string;
        name: string;
        status: string;
        replied_at: string | null;
      };
    }>;
  }>({
    queryKey: ['outreach', 'campaigns', campaignId, 'reply-forensics'],
    queryFn: () =>
      apiFetch(`/api/outreach/campaigns/${campaignId}/reply-forensics`),
    enabled: enabled && campaignId != null && campaignId > 0,
    staleTime: 15 * 1000,
  });
}

export function useCampaignErrors(
  campaignId: number | null,
  { includeTest = false }: { includeTest?: boolean } = {},
) {
  return useQuery<CampaignErrorRow[]>({
    queryKey: ['outreach', 'campaigns', campaignId, 'errors', includeTest],
    queryFn: () => {
      const qs = includeTest ? '?include_test=true' : '';
      return apiFetch<CampaignErrorRow[]>(
        `/api/outreach/campaigns/${campaignId}/errors${qs}`,
      );
    },
    enabled: campaignId != null && campaignId > 0,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useRecipientTimeline(
  campaignId: number | null,
  recipientId: number | null,
) {
  return useQuery<TimelineEvent[]>({
    queryKey: [
      'outreach', 'campaigns', campaignId,
      'recipients', recipientId, 'events',
    ],
    queryFn: () =>
      apiFetch<TimelineEvent[]>(
        `/api/outreach/campaigns/${campaignId}/recipients/${recipientId}/events`,
      ),
    enabled:
      campaignId != null && campaignId > 0
      && recipientId != null && recipientId > 0,
    staleTime: 10 * 1000,
  });
}

export function useRetryRecipient() {
  const qc = useQueryClient();
  return useMutation<
    RecipientRetryResult, Error,
    { campaignId: number; recipientId: number }
  >({
    mutationFn: ({ campaignId, recipientId }) =>
      apiFetch<RecipientRetryResult>(
        `/api/outreach/campaigns/${campaignId}/recipients/${recipientId}/retry`,
        { method: 'POST' },
      ),
    onSuccess: (_, { campaignId }) => {
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'errors'],
      });
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'recipients'],
      });
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'ticks'],
      });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Reactivate paused/finished campaign (PR #11)
// ─────────────────────────────────────────────────────────────────────────

export function useReactivateCampaign() {
  const qc = useQueryClient();
  return useMutation<
    ReactivateCampaignResult, Error,
    { campaignId: number; body: ReactivateCampaignRequest }
  >({
    mutationFn: ({ campaignId, body }) =>
      apiFetch<ReactivateCampaignResult>(
        `/api/outreach/campaigns/${campaignId}/reactivate`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: (_, { campaignId }) => {
      qc.invalidateQueries({ queryKey: ['outreach', 'campaigns'] });
      qc.invalidateQueries({ queryKey: ['outreach', 'campaigns', campaignId] });
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'recipients'],
      });
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'ticks'],
      });
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'audit'],
      });
    },
  });
}


// ─────────────────────────────────────────────────────────────────────────
// Duplicate campaign (PR #10)
// ─────────────────────────────────────────────────────────────────────────

export function useDuplicateCampaign() {
  const qc = useQueryClient();
  return useMutation<Campaign, Error, number>({
    mutationFn: (campaignId) =>
      apiFetch<Campaign>(
        `/api/outreach/campaigns/${campaignId}/duplicate`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['outreach', 'campaigns'] });
    },
  });
}


// ─────────────────────────────────────────────────────────────────────────
// Campaign audit (Activity tab)
// ─────────────────────────────────────────────────────────────────────────

export function useCampaignAudit(campaignId: number | null) {
  return useQuery<CampaignAuditResponse>({
    queryKey: ['outreach', 'campaigns', campaignId, 'audit'],
    queryFn: () =>
      apiFetch<CampaignAuditResponse>(
        `/api/outreach/campaigns/${campaignId}/audit`,
      ),
    enabled: campaignId != null,
    staleTime: 15 * 1000,
  });
}


// ─────────────────────────────────────────────────────────────────────────
// RSVP approval queue
// ─────────────────────────────────────────────────────────────────────────

export function useSetCampaignEvent() {
  const qc = useQueryClient();
  return useMutation<
    { status: string; updated_fields: string[] },
    Error,
    { campaignId: number; body: CampaignEventConfig }
  >({
    mutationFn: ({ campaignId, body }) =>
      apiFetch<{ status: string; updated_fields: string[] }>(
        `/api/outreach/campaigns/${campaignId}/event`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    onSuccess: (_, { campaignId }) => {
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId],
      });
      qc.invalidateQueries({ queryKey: ['outreach', 'campaigns'] });
    },
  });
}

export function useTestSendEvent() {
  return useMutation<
    EventTestSendResult,
    Error,
    { campaignId: number; testEmail: string }
  >({
    mutationFn: ({ campaignId, testEmail }) =>
      apiFetch<EventTestSendResult>(
        `/api/outreach/campaigns/${campaignId}/event/test-send`,
        {
          method: 'POST',
          body: JSON.stringify({ test_email: testEmail }),
        },
      ),
  });
}

export function useCampaignRsvpApprovals(
  campaignId: number | null,
  statusFilter?: string,
) {
  const qs = statusFilter
    ? `?status_filter=${encodeURIComponent(statusFilter)}`
    : '';
  return useQuery<RsvpApprovalsResponse>({
    queryKey: ['outreach', 'campaigns', campaignId, 'rsvp-approvals', statusFilter || ''],
    queryFn: () =>
      apiFetch<RsvpApprovalsResponse>(
        `/api/outreach/campaigns/${campaignId}/rsvp-approvals${qs}`,
      ),
    enabled: campaignId != null && campaignId > 0,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useQueueRsvpManually() {
  const qc = useQueryClient();
  return useMutation<
    RsvpApprovalActionResult,
    Error,
    { campaignId: number; recipientId: number; action: RsvpManualAction }
  >({
    mutationFn: ({ campaignId, recipientId, action }) =>
      apiFetch<RsvpApprovalActionResult>(
        `/api/outreach/campaigns/${campaignId}/recipients/${recipientId}/queue-rsvp`,
        { method: 'POST', body: JSON.stringify({ action }) },
      ),
    onSuccess: (_, { campaignId }) => {
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'rsvp-approvals'],
      });
    },
  });
}

export function useApproveRsvp() {
  const qc = useQueryClient();
  return useMutation<
    RsvpApprovalActionResult,
    Error,
    {
      approvalId: number;
      campaignId: number;
      override_status_event?: string | null;
      override_send_invite?: boolean | null;
    }
  >({
    mutationFn: ({ approvalId, override_status_event, override_send_invite }) =>
      apiFetch<RsvpApprovalActionResult>(
        `/api/outreach/rsvp-approvals/${approvalId}/approve`,
        {
          method: 'POST',
          body: JSON.stringify({
            override_status_event: override_status_event ?? null,
            override_send_invite: override_send_invite ?? null,
          }),
        },
      ),
    onSuccess: (_, { campaignId }) => {
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'rsvp-approvals'],
      });
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'recipients'],
      });
    },
  });
}

export function useDismissRsvp() {
  const qc = useQueryClient();
  return useMutation<
    RsvpApprovalActionResult,
    Error,
    { approvalId: number; campaignId: number }
  >({
    mutationFn: ({ approvalId }) =>
      apiFetch<RsvpApprovalActionResult>(
        `/api/outreach/rsvp-approvals/${approvalId}/dismiss`,
        { method: 'POST' },
      ),
    onSuccess: (_, { campaignId }) => {
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'rsvp-approvals'],
      });
    },
  });
}

export function useBulkApproveRsvp() {
  const qc = useQueryClient();
  return useMutation<
    RsvpBulkApproveResult,
    Error,
    { approvalIds: number[]; campaignId: number }
  >({
    mutationFn: ({ approvalIds }) =>
      apiFetch<RsvpBulkApproveResult>(
        '/api/outreach/rsvp-approvals/bulk-approve',
        {
          method: 'POST',
          body: JSON.stringify({ approval_ids: approvalIds }),
        },
      ),
    onSuccess: (_, { campaignId }) => {
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'rsvp-approvals'],
      });
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'recipients'],
      });
    },
  });
}


// ─────────────────────────────────────────────────────────────────────────
// Step deletion-impact preview (powers the active-campaign delete confirm)
// ─────────────────────────────────────────────────────────────────────────

export function useStepDeletionImpact(
  campaignId: number | null,
  stepId: number | null,
  enabled: boolean = false,
) {
  return useQuery<StepDeletionImpact>({
    queryKey: ['outreach', 'campaigns', campaignId, 'steps', stepId, 'deletion-impact'],
    queryFn: () =>
      apiFetch<StepDeletionImpact>(
        `/api/outreach/campaigns/${campaignId}/steps/${stepId}/deletion-impact`,
      ),
    enabled:
      enabled
      && campaignId != null && campaignId > 0
      && stepId != null && stepId > 0,
    staleTime: 5 * 1000,
  });
}

// Preview how many finished recipients a new step at (step_order,
// delay_minutes) would re-activate. Powers the "Also send to finished
// recipients?" checkbox in the New step modal.
export function useFinishedRecipientImpact(
  campaignId: number | null,
  stepOrder: number,
  delayMinutes: number,
  enabled: boolean = false,
) {
  return useQuery<FinishedRecipientImpact>({
    queryKey: [
      'outreach', 'campaigns', campaignId, 'steps',
      'finished-recipient-impact', stepOrder, delayMinutes,
    ],
    queryFn: () =>
      apiFetch<FinishedRecipientImpact>(
        `/api/outreach/campaigns/${campaignId}/steps/finished-recipient-impact`
        + `?step_order=${stepOrder}&delay_minutes=${delayMinutes}`,
      ),
    enabled:
      enabled
      && campaignId != null && campaignId > 0
      && stepOrder >= 1,
    staleTime: 5 * 1000,
  });
}

// Resume-finished bulk action. Both are POST so the recipient_ids selection
// rides in the body. The preview is a mutation (not a query) because it POSTs;
// callers fire it on confirm-modal open to show the blast radius.
export function useResumeFinishedPreview() {
  return useMutation<
    ResumeFinishedImpact, Error,
    { campaignId: number; recipientIds?: number[] }
  >({
    mutationFn: ({ campaignId, recipientIds }) =>
      apiFetch<ResumeFinishedImpact>(
        `/api/outreach/campaigns/${campaignId}/recipients/resume-finished/preview`,
        {
          method: 'POST',
          body: JSON.stringify({ recipient_ids: recipientIds ?? null }),
        },
      ),
  });
}

export function useResumeFinishedRecipients() {
  const qc = useQueryClient();
  return useMutation<
    ResumeFinishedResult, Error,
    { campaignId: number; recipientIds?: number[] }
  >({
    mutationFn: ({ campaignId, recipientIds }) =>
      apiFetch<ResumeFinishedResult>(
        `/api/outreach/campaigns/${campaignId}/recipients/resume-finished`,
        {
          method: 'POST',
          body: JSON.stringify({ recipient_ids: recipientIds ?? null }),
        },
      ),
    onSuccess: (_, { campaignId }) => {
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'recipients'],
      });
      qc.invalidateQueries({
        queryKey: ['outreach', 'campaigns', campaignId, 'stats'],
      });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Outreach assets (hero images / banners for step bodies)
// ─────────────────────────────────────────────────────────────────────────

export function useOutreachAssets() {
  return useQuery<OutreachAsset[]>({
    queryKey: ['outreach', 'assets'],
    queryFn: () => apiFetch<OutreachAsset[]>('/api/outreach/assets'),
    staleTime: 30 * 1000,
  });
}

export function useUploadOutreachAsset() {
  const qc = useQueryClient();
  return useMutation<OutreachAssetUploadResult, Error, File>({
    mutationFn: (file) => {
      // Use apiFetch so the Entra Bearer flow runs in prod -- the
      // earlier bare-fetch + X-Api-Key shortcut worked in dev (API key
      // fallback enabled) but 401'd in prod where Entra is required.
      // apiFetch detects FormData bodies and skips its JSON Content-
      // Type header automatically; the browser supplies the correct
      // multipart boundary.
      const fd = new FormData();
      fd.append('file', file);
      return apiFetch<OutreachAssetUploadResult>(
        '/api/outreach/assets',
        { method: 'POST', body: fd },
      );
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outreach', 'assets'] }),
  });
}

export function useDeleteOutreachAsset() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (filename) =>
      apiFetch<void>(
        `/api/outreach/assets/${encodeURIComponent(filename)}`,
        { method: 'DELETE' },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['outreach', 'assets'] }),
  });
}


// ─────────────────────────────────────────────────────────────────────────
// Form responses (top-level Outreach > Forms tab)
// ─────────────────────────────────────────────────────────────────────────

export function useFormResponses(filters: {
  campaignId?: number | null;
  attending?: 'yes' | 'no' | null;
} = {}) {
  const params = new URLSearchParams();
  if (filters.campaignId != null) {
    params.set('campaign_id', String(filters.campaignId));
  }
  if (filters.attending) {
    params.set('attending', filters.attending);
  }
  const qs = params.toString();
  return useQuery<FormResponsesResponse>({
    queryKey: ['outreach', 'form-responses', filters.campaignId ?? '', filters.attending ?? ''],
    queryFn: () =>
      apiFetch<FormResponsesResponse>(
        `/api/outreach/form-responses${qs ? `?${qs}` : ''}`,
      ),
    staleTime: 30 * 1000,
    // Auto-refresh while the Forms tab is open. Same cadence as the
    // recipients/stats/ticks polling so a fresh RSVP appears within
    // 30s without a manual reload.
    refetchInterval: 30 * 1000,
  });
}


// ─────────────────────────────────────────────────────────────────────────
// Forms (definitions, M:N to lists)
// ─────────────────────────────────────────────────────────────────────────

export function useForms() {
  return useQuery<OutreachForm[]>({
    queryKey: ['outreach', 'forms'],
    queryFn: () => apiFetch<OutreachForm[]>('/api/outreach/forms'),
    staleTime: 30 * 1000,
  });
}

export function useCreateForm() {
  const qc = useQueryClient();
  return useMutation<OutreachForm, Error, FormIn>({
    mutationFn: (body) =>
      apiFetch<OutreachForm>('/api/outreach/forms', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach', 'forms'] }),
  });
}

export function useUpdateForm() {
  const qc = useQueryClient();
  return useMutation<
    OutreachForm, Error,
    { formId: number; body: FormUpdate }
  >({
    mutationFn: ({ formId, body }) =>
      apiFetch<OutreachForm>(`/api/outreach/forms/${formId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach', 'forms'] }),
  });
}

export function useDeleteForm() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (formId) =>
      apiFetch<void>(`/api/outreach/forms/${formId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach', 'forms'] }),
  });
}


// ─────────────────────────────────────────────────────────────────────────
// Sender subscription provisioning (manual lever for the bug-fix where
// senders added via seed had no Graph subscription -- inbound replies
// were silently dropped at the Graph layer.)
// ─────────────────────────────────────────────────────────────────────────

export function useProvisionSenderSubscription() {
  const qc = useQueryClient();
  return useMutation<OutreachSender, Error, string>({
    mutationFn: (email) =>
      apiFetch<OutreachSender>(
        `/api/outreach/senders/${encodeURIComponent(email)}/provision-subscription`,
        { method: 'POST' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach', 'senders'] }),
  });
}


// ─────────────────────────────────────────────────────────────────────────
// Step templates (reusable subject + body)
// ─────────────────────────────────────────────────────────────────────────

interface ListTemplatesArgs {
  archived?: boolean;
  search?: string;
  /**
   * Comma-separated compat values: 'per_person', 'per_organization',
   * 'both'. The Load-template picker passes
   * `${campaign.send_mode},both` to show only templates that render in
   * the current step's mode.
   */
  compat?: string;
}

export function useStepTemplates(args: ListTemplatesArgs = {}) {
  const params = new URLSearchParams();
  if (args.archived) params.set('archived', 'true');
  if (args.search) params.set('search', args.search);
  if (args.compat) params.set('compat', args.compat);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return useQuery<StepTemplate[]>({
    queryKey: ['outreach', 'templates', 'list', args.archived || false, args.search || '', args.compat || ''],
    queryFn: () =>
      apiFetch<StepTemplate[]>(`/api/outreach/templates${qs}`),
    staleTime: 30 * 1000,
  });
}

export function useStepTemplate(id: number | null) {
  return useQuery<StepTemplate>({
    queryKey: ['outreach', 'templates', id],
    queryFn: () =>
      apiFetch<StepTemplate>(`/api/outreach/templates/${id}`),
    enabled: id != null && id > 0,
  });
}

export function useCreateStepTemplate() {
  const qc = useQueryClient();
  return useMutation<StepTemplate, Error, StepTemplateIn>({
    mutationFn: (body) =>
      apiFetch<StepTemplate>(`/api/outreach/templates`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach', 'templates'] }),
  });
}

export function useUpdateStepTemplate() {
  const qc = useQueryClient();
  return useMutation<
    StepTemplate, Error,
    { id: number; body: StepTemplateUpdate }
  >({
    mutationFn: ({ id, body }) =>
      apiFetch<StepTemplate>(`/api/outreach/templates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach', 'templates'] }),
  });
}

export function useDeleteStepTemplate() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) =>
      apiFetch<void>(`/api/outreach/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach', 'templates'] }),
  });
}

export function useDuplicateStepTemplate() {
  const qc = useQueryClient();
  return useMutation<StepTemplate, Error, number>({
    mutationFn: (id) =>
      apiFetch<StepTemplate>(`/api/outreach/templates/${id}/duplicate`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach', 'templates'] }),
  });
}
